<template>
  <div class="audit-container">
    <AppSectionHero
      title="Audit Log"
      subtitle="Track system activity, access events, and administrative actions in one place."
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectHeroFilter"
    />

    <div v-if="errorMessage" class="status-banner status-banner--warning">
      {{ errorMessage }}
    </div>

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="filter-bar toolbar">
          <div class="search-box" style="border: 1px solid var(--app-border-strong); background: linear-gradient(180deg, #fff 0%, #f8fafc 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,0.72); border-radius: var(--app-radius-md);">
            <span class="search-icon">🔍</span>
            <input v-model="searchQuery" placeholder="Search logs by user, action, target, or module..." style="border: none; background: transparent; box-shadow: none; outline: none;">
          </div>
          <select v-model="severityFilter" class="filter-select">
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
          <input v-model="selectedDate" type="date" class="date-input">
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm table-card">
      <div class="table-header">
        <span>Showing {{ paginatedLogs.length }} of {{ filteredLogs.length }} logs</span>
        <button class="export-btn" @click="exportLogs">Export CSV</button>
      </div>
      <CCardBody class="p-0">
        <div class="table-wrapper">
          <table class="audit-table">
            <thead>
              <tr>
                <th @click="sortBy('timestamp')" class="sortable">Timestamp {{ sortIcon('timestamp') }}</th>
                <th @click="sortBy('user')" class="sortable">User {{ sortIcon('user') }}</th>
                <th @click="sortBy('module')" class="sortable">Module {{ sortIcon('module') }}</th>
                <th @click="sortBy('action')" class="sortable">Action {{ sortIcon('action') }}</th>
                <th>Target</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in paginatedLogs" :key="item.id">
                <td class="timestamp-cell">{{ formatDateTime(item.timestamp) }}</td>
                <td class="user-cell">{{ item.user }}</td>
                <td class="module-cell">{{ item.module }}</td>
                <td class="action-cell">{{ item.action }}</td>
                <td class="target-cell">{{ displayTarget(item) }}</td>
                <td class="status-cell">
                  <span class="status-pill" :class="'status-pill--' + item.status">{{ item.status }}</span>
                </td>
                <td class="actions-cell">
                  <button class="detail-btn audit-detail-btn" @click="openLog(item)">Details</button>
                </td>
              </tr>
              <tr v-if="!paginatedLogs.length">
                <td colspan="7" class="empty-cell">No audit records match the current filters.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="filteredLogs.length > pageSize" class="pagination">
          <div class="pagination-controls">
            <button v-if="currentPage > 1" class="page-btn" @click="currentPage--">← Previous</button>
            <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">Next →</button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="selectedLog" class="modal-overlay" @click.self="selectedLog = null">
      <div class="modal-container view-modal">
        <div class="modal-header">
          <h2>Audit Log Details</h2>
          <button class="close-btn" @click="selectedLog = null">✕</button>
        </div>
        <div class="modal-body">
          <div class="detail-section">
            <div class="detail-row">
              <span class="detail-label">Timestamp:</span>
              <span class="detail-value">{{ formatFullDateTime(selectedLog.timestamp) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">User:</span>
              <span class="detail-value">{{ selectedLog.user }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Module:</span>
              <span class="detail-value">{{ selectedLog.module }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Action:</span>
              <span class="detail-value">{{ selectedLog.action }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Target:</span>
              <span class="detail-value">{{ displayTarget(selectedLog) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status:</span>
              <span class="detail-value">
                <span class="status-pill" :class="'status-pill--' + selectedLog.status">{{ selectedLog.status }}</span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Actor Email:</span>
              <span class="detail-value">{{ selectedLog.actorEmail || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Resource Type:</span>
              <span class="detail-value">{{ formatResourceType(selectedLog.resourceType) }}</span>
            </div>
            <div class="detail-row detail-row--stacked">
              <span class="detail-label">Context:</span>
              <span class="detail-value">{{ buildContextSummary(selectedLog) }}</span>
            </div>
          </div>

          <div class="modal-actions">
            <button class="close-detail-btn" @click="selectedLog = null">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import Service from '@/service/api'

function normalizeStatus(value) {
  const text = String(value || '').toLowerCase()
  if (text.includes('fail') || text.includes('error')) return 'error'
  if (text.includes('warn') || text.includes('alert')) return 'warning'
  return 'success'
}

export default {
  name: 'AuditLogPage',
  components: {
    AppSectionHero
  },
  data() {
    return {
      logs: [],
      agreementCodeMap: {},
      selectedLog: null,
      errorMessage: '',
      searchQuery: '',
      activeHeroFilter: 'all',
      severityFilter: 'all',
      selectedDate: '',
      currentPage: 1,
      pageSize: 10,
      sortField: 'timestamp',
      sortDirection: 'desc'
    }
  },
  computed: {
    heroStats() {
      return [
        {
          label: 'Total Logs',
          value: this.filteredLogs.length,
          hint: 'Current filtered records',
          icon: 'cil-list',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.activeHeroFilter === 'all'
        },
        {
          label: 'Sign-In Events',
          value: this.signInCount,
          hint: 'Authentication activity',
          icon: 'cil-user',
          iconClass: 'app-section-stat__icon--active',
          clickable: true,
          active: this.activeHeroFilter === 'sign-in'
        },
        {
          label: 'Permission Changes',
          value: this.permissionCount,
          hint: 'Access control updates',
          icon: 'cil-lock-locked',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.activeHeroFilter === 'permission'
        }
      ]
    },
    filteredLogs() {
      let rows = [...this.logs]
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        rows = rows.filter(item =>
          item.user.toLowerCase().includes(query) ||
          item.action.toLowerCase().includes(query) ||
          this.displayTarget(item).toLowerCase().includes(query) ||
          item.module.toLowerCase().includes(query)
        )
      }
      if (this.severityFilter !== 'all') rows = rows.filter(item => item.status === this.severityFilter)
      if (this.selectedDate) {
        const wanted = new Date(this.selectedDate).toDateString()
        rows = rows.filter(item => new Date(item.timestamp).toDateString() === wanted)
      }
      if (this.activeHeroFilter === 'sign-in') {
        rows = rows.filter(item => this.isSignInLog(item))
      } else if (this.activeHeroFilter === 'permission') {
        rows = rows.filter(item => this.isPermissionLog(item))
      }
      return rows.sort((left, right) => this.compareRows(left, right))
    },
    paginatedLogs() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredLogs.slice(start, start + this.pageSize)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredLogs.length / this.pageSize))
    },
    signInCount() {
      return this.filteredLogs.filter(item => this.isSignInLog(item)).length
    },
    permissionCount() {
      return this.filteredLogs.filter(item => this.isPermissionLog(item)).length
    },
    warningCount() {
      return this.filteredLogs.filter(item => item.status !== 'success').length
    }
  },
  watch: {
    searchQuery() { this.currentPage = 1 },
    severityFilter() { this.currentPage = 1 },
    selectedDate() { this.currentPage = 1 }
  },
  mounted() {
    this.fetchLogs()
    this.fetchAgreementCodes()
  },
  methods: {
    isSignInLog(item) {
      return String(item && item.action || '').toLowerCase().includes('login') || String(item && item.action || '').toLowerCase().includes('sign')
    },
    isPermissionLog(item) {
      return String(item && item.module || '').toLowerCase() === 'permissions' || String(item && item.action || '').toLowerCase().includes('permission')
    },
    selectHeroFilter(stat) {
      const label = String(stat && stat.label || '').toLowerCase()
      const nextFilter = label === 'sign-in events'
        ? 'sign-in'
        : label === 'permission changes'
          ? 'permission'
          : 'all'
      this.activeHeroFilter = this.activeHeroFilter === nextFilter ? 'all' : nextFilter
    },
    getAgreementReferenceId(item) {
      if (!item || typeof item !== 'object') return ''
      const metaAgreementId = item.meta && item.meta.agreementId ? String(item.meta.agreementId) : ''
      if (metaAgreementId) return metaAgreementId
      const resourceType = String(item.resourceType || '').toLowerCase()
      if (resourceType.includes('agreement')) return String(item.resourceId || item.target || '')
      return ''
    },
    displayTarget(item) {
      if (!item || typeof item !== 'object') return '-'
      const agreementId = this.getAgreementReferenceId(item)
      if (agreementId && this.agreementCodeMap[agreementId]) return this.agreementCodeMap[agreementId]
      const target = String(item.target || item.resourceId || '').trim()
      return target || '-'
    },
    formatResourceType(value) {
      const text = String(value || '').trim()
      if (!text) return '-'
      return text.replace(/_/g, ' ')
    },
    buildContextSummary(item) {
      if (!item || typeof item !== 'object') return '-'
      const meta = item.meta && typeof item.meta === 'object' ? item.meta : {}
      const parts = []
      if (meta.agreementTitle) parts.push(`Agreement: ${meta.agreementTitle}`)
      if (meta.targetLanguage) parts.push(`Language: ${String(meta.targetLanguage).toUpperCase()}`)
      if (meta.translationMode) parts.push(`Mode: ${meta.translationMode}`)
      if (meta.section) parts.push(`Section: ${meta.section}`)
      if (meta.decision) parts.push(`Decision: ${meta.decision}`)
      if (meta.note) parts.push(`Note: ${meta.note}`)
      return parts.length ? parts.join(' | ') : 'No additional context saved.'
    },
    compareRows(left, right) {
      let a = left[this.sortField]
      let b = right[this.sortField]
      if (this.sortField === 'timestamp') {
        a = new Date(a).getTime()
        b = new Date(b).getTime()
      } else {
        a = String(a || '').toLowerCase()
        b = String(b || '').toLowerCase()
      }
      if (a < b) return this.sortDirection === 'asc' ? -1 : 1
      if (a > b) return this.sortDirection === 'asc' ? 1 : -1
      return 0
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }
    },
    sortIcon(field) {
      if (this.sortField !== field) return '↕'
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },
    formatDateTime(value) {
      return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatFullDateTime(value) {
      return new Date(value).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    openLog(item) {
      this.selectedLog = item
    },
    buildCsv(rows) {
      return rows.map(row => row.map(value => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`).join(',')).join('\n')
    },
    exportLogs() {
      const rows = [
        ['Timestamp', 'User', 'Module', 'Action', 'Target', 'Status'],
        ...this.filteredLogs.map(item => [this.formatFullDateTime(item.timestamp), item.user, item.module, item.action, this.displayTarget(item), item.status])
      ]
      const blob = new Blob([this.buildCsv(rows)], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'audit-log.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    mapLog(item, index) {
      const createdAt = item && (item.timestamp || item.createdAt || item.datetime || item.at)
      const resourceTarget = item && item.resourceType
        ? `${item.resourceType}${item.resourceId ? `:${item.resourceId}` : ''}`
        : '-'
      return {
        id: String(item && (item._id || item.id || index)),
        timestamp: createdAt || new Date().toISOString(),
        user: String(item && (item.user || item.actorName || item.actor || item.actorEmail || item.email || item.createdBy || 'System')),
        actorEmail: String(item && (item.actorEmail || item.email || '')),
        module: String(item && (item.module || item.area || item.service || 'system')).toLowerCase(),
        action: String(item && (item.action || item.event || item.message || 'Event')),
        target: String(item && (item.target || item.resource || item.subject || resourceTarget)),
        resourceType: String(item && (item.resourceType || '')),
        resourceId: String(item && (item.resourceId || '')),
        status: normalizeStatus(item && (item.status || item.level || item.action)),
        meta: item && item.meta && typeof item.meta === 'object' ? item.meta : {}
      }
    },
    async fetchAgreementCodes() {
      try {
        const response = await Service.adminReview('agreements')
        const rows = Array.isArray(response && response.data && response.data.data) ? response.data.data : []
        this.agreementCodeMap = rows.reduce((accumulator, item) => {
          const id = String(item && (item.id || item._id) || '')
          const code = String(item && item.documentCode || '')
          if (id && code) accumulator[id] = code
          return accumulator
        }, {})
      } catch (error) {
        this.agreementCodeMap = {}
      }
    },
    async fetchLogs() {
      try {
        const response = await Service.audit('logs')
        const data = Array.isArray(response && response.data && response.data.data) ? response.data.data : []
        if (data.length) {
          this.logs = data.map((item, index) => this.mapLog(item, index))
          this.errorMessage = ''
        } else {
          this.logs = []
          this.errorMessage = ''
        }
      } catch (error) {
        this.logs = []
        this.errorMessage = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : 'Audit API could not be loaded right now.'
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("audit-container");
@include workspace-toolbar-search;
@include workspace-button-system;

// Main container - prevent horizontal overflow
.audit-container { 
  padding: 24px;
  overflow-x: hidden;
  max-width: 100%;
  width: 100%;
}

// Responsive filter bar
.filter-bar, .table-header, .pagination, .modal-header { 
  display: flex; 
  gap: 12px; 
}

.filter-bar, .table-header, .modal-header { 
  align-items: center; 
  justify-content: space-between; 
  flex-wrap: wrap;
}

.pagination { 
  align-items: center; 
  justify-content: center; 
  position: relative;
  flex-wrap: wrap;
  gap: 12px;
  
  .page-btn:first-child { 
    left: 18px; 
    position: absolute; 
  }
  
  .page-btn:last-child { 
    position: absolute; 
    right: 18px; 
  }
}

.status-banner { 
  margin-bottom: 16px; 
  padding: 12px 14px; 
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  
  &--warning { 
    background: #fff7e6; 
    color: #9a6700; 
    border: 1px solid #f8df9c; 
  }
}

// Card containers - prevent overflow
.table-card { 
  background: rgba(255, 255, 255, 0.94); 
  border: 1px solid #e7edf4; 
  border-radius: 22px; 
  box-shadow: 0 18px 38px rgba(44, 52, 71, 0.08);
  overflow: hidden;
  width: 100%;
}

.filter-shell {
  width: 100%;
}

.filter-bar { 
  flex-wrap: wrap;
  
  > * {
    flex: 0 0 auto;
  }
  
  .search-box {
    flex: 1;
    min-width: 200px;
  }
}

.table-header { 
  padding: 16px 18px;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination { 
  padding: 16px 18px 26px;
}

// Table wrapper with horizontal scroll
.table-wrapper { 
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

// Responsive table with minimum width
.audit-table { 
  width: 100%; 
  border-collapse: collapse;
  min-width: 800px;
  
  th, td {
    border-top: 1px solid #eef1f6;
    padding: 14px 16px;
    text-align: left;
    white-space: normal;
    word-break: break-word;
    vertical-align: middle;
  }
  
  th {
    color: #52606d;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: #fafbfc;
    white-space: nowrap;
  }
  
  // Specific column widths for better control
  th:nth-child(1), td:nth-child(1) { 
    min-width: 160px; 
    max-width: 180px;
  }
  
  th:nth-child(2), td:nth-child(2) { 
    min-width: 120px; 
    max-width: 150px;
  }
  
  th:nth-child(3), td:nth-child(3) { 
    min-width: 100px; 
    max-width: 130px;
  }
  
  th:nth-child(4), td:nth-child(4) { 
    min-width: 180px; 
    max-width: 220px;
  }
  
  th:nth-child(5), td:nth-child(5) { 
    min-width: 150px; 
    max-width: 200px;
  }
  
  th:nth-child(6), td:nth-child(6) { 
    min-width: 100px; 
    max-width: 110px;
  }
  
  th:nth-child(7), td:nth-child(7) { 
    min-width: 80px; 
    max-width: 100px;
  }
  
  // Text truncation for long content
  .action-cell, .target-cell {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.sortable { 
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f1f5f9;
  }
}

.status-pill { 
  border-radius: 999px; 
  display: inline-block; 
  font-size: 12px; 
  font-weight: 700; 
  padding: 6px 10px; 
  text-transform: capitalize; 
  white-space: nowrap;
  
  &--success { 
    background: #e8f7ee; 
    color: #1f7a4d; 
  }
  
  &--warning { 
    background: #fff5db; 
    color: #9a6700; 
  }
  
  &--error { 
    background: #fff1f2; 
    color: #b42318; 
  }
}

.empty-cell { 
  color: #718096; 
  padding: 48px 24px; 
  text-align: center; 
}

.pagination {
  align-items: center;
  background: #fafbfc;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  padding: 20px 20px 28px;
}

.pagination-controls {
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  position: relative;
}

.pagination-controls .page-btn:first-child {
  left: 18px;
  position: absolute;
}

.pagination-controls .page-btn:last-child {
  position: absolute;
  right: 0;
}

.page-indicator {
  color: #475569;
  font-size: 13px;
  font-weight: 500;
}

.audit-detail-btn {
  min-height: 42px;
  min-width: 116px;
  padding: 10px 18px;
}

// Modal styles - prevent overflow
.modal-overlay { 
  align-items: center; 
  backdrop-filter: blur(4px); 
  background: rgba(15, 23, 42, 0.5); 
  display: flex; 
  inset: 0; 
  justify-content: center; 
  padding: 24px; 
  position: fixed; 
  z-index: 3000;
}

.modal-container { 
  background: white; 
  border-radius: 20px; 
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); 
  max-height: calc(100vh - 48px); 
  max-width: calc(100vw - 48px);
  width: min(720px, calc(100vw - 48px));
  overflow-y: auto;
  overflow-x: hidden;
  
  &.view-modal { 
    max-width: 720px; 
  }
}

.modal-header { 
  padding: 20px 24px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-bottom: 1px solid #eef1f6;
  
  h2 { 
    margin: 0; 
    min-width: 0; 
    overflow-wrap: anywhere;
    font-size: 1.25rem;
    font-weight: 600;
  }
}

.modal-body { 
  overflow-x: hidden; 
  padding: 24px;
}

.close-btn { 
  align-items: center; 
  background: none; 
  border: none; 
  border-radius: 50%; 
  color: #94a3b8; 
  cursor: pointer; 
  display: flex; 
  font-size: 20px; 
  height: 32px; 
  justify-content: center; 
  transition: all 0.2s; 
  width: 32px;
  flex-shrink: 0;
  
  &:hover { 
    background: #f1f5f9; 
    color: #8c1515; 
  }
}

.detail-section { 
  display: flex; 
  flex-direction: column; 
  gap: 0; 
}

.detail-row { 
  align-items: flex-start; 
  border-bottom: 1px solid #eef1f6; 
  display: flex; 
  gap: 16px; 
  justify-content: space-between; 
  padding: 14px 0;
  
  &:last-child {
    border-bottom: none;
  }
}

.detail-row--stacked { 
  align-items: flex-start; 
  flex-direction: column; 
  justify-content: flex-start; 
  
  .detail-label,
  .detail-value { 
    text-align: left; 
    width: 100%;
  }
}

.detail-label { 
  color: #64748b; 
  flex: 0 0 140px; 
  font-size: 13px; 
  font-weight: 600; 
  max-width: 140px; 
  min-width: 140px;
}

.detail-value { 
  color: #2d3748; 
  flex: 1 1 auto; 
  font-size: 14px; 
  font-weight: 500; 
  min-width: 0; 
  overflow-wrap: anywhere; 
  text-align: right; 
  word-break: break-word;
}

.modal-actions { 
  display: flex; 
  justify-content: flex-end; 
  margin-top: 24px;
  gap: 12px;
}

.close-detail-btn { 
  font-size: 14px;
  padding: 8px 20px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e2e8f0;
  }
}

.detail-btn {
  padding: 6px 12px;
  font-size: 13px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
}

.export-btn {
  padding: 6px 16px;
  font-size: 13px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #059669;
  }
}

.page-btn {
  padding: 6px 12px;
  font-size: 13px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Search box styling
.search-box {
  position: relative;
  display: flex;
  align-items: center;
  
  .search-icon {
    position: absolute;
    left: 12px;
    font-size: 14px;
    opacity: 0.6;
  }
  
  input {
    padding: 8px 12px 8px 32px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
    transition: all 0.2s;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
}

.filter-select, .date-input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
}

// Responsive breakpoints
@media (max-width: 768px) {
  .audit-container {
    padding: 16px;
  }
  
  .filter-bar {
    flex-direction: column;
    
    .search-box {
      width: 100%;
    }
    
    .filter-select, .date-input {
      width: 100%;
    }
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .pagination {
    flex-direction: column;
    
    .page-btn:first-child,
    .page-btn:last-child {
      position: static;
    }
  }
  
  .detail-row {
    flex-direction: column;
    gap: 8px;
    
    .detail-label {
      text-align: left;
      width: 100%;
    }
    
    .detail-value {
      text-align: left;
      width: 100%;
    }
  }
  
  .modal-container {
    max-width: calc(100vw - 32px);
    width: calc(100vw - 32px);
  }
}

@media (max-width: 640px) {
  .audit-table {
    min-width: 700px;
  }

  .pagination-controls {
    width: 100%;
  }

  .pagination-controls .page-btn:first-child,
  .pagination-controls .page-btn:last-child {
    position: static;
  }
  
  .modal-header h2 {
    font-size: 1.1rem;
  }
  
  .modal-body {
    padding: 16px;
  }
}
</style>


<style lang="scss" scoped>
@import '../shared/workspace-consistency.scss';

.pagination {
  align-items: center;
  background: #fafbfc;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  padding: 20px 20px 28px;
}
.pagination-controls {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
  justify-content: center;
  position: relative;
}
.pagination-controls .page-btn:first-child {
  left: 18px;
  position: absolute;
}
.pagination-controls .page-btn:last-child {
  position: absolute;
  right: 0;
}
.page-indicator {
  color: #475569;
  font-size: 13px;
  font-weight: 500;
}
@media (max-width: 640px) {
  .pagination-controls {
    width: 100%;
  }
  .pagination-controls .page-btn:first-child,
  .pagination-controls .page-btn:last-child {
    position: static;
  }
}
</style>
